import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";
import Modal from "react-modal";
import Swal from "sweetalert2/src/sweetalert2.js";
import { getKycURL, getKycVerification, mintNFT } from "../../../api";
import { useDynamic } from "../../../hooks/DynamicProvider";
import logo from "../../../assets/images/onboarding/access-logo.png";
import "./kyc.css";
import InfoModal from "../../../components/InfoModal";

Modal.setAppElement("#root");
const socket = io(process.env.REACT_APP_SERVER); // Replace with your server URL in production

const KYC = ({ onNext, isMobile, containerHeight }) => {
  const { isLoading, userInfo, isLoggedIn, checkUserLoggedIn } = useDynamic();
  const [verificationStatus, setVerificationStatus] = useState("");
  const [kycData, setKycData] = useState();
  const [loading, setLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const kyc_param = searchParams.get("verify");

  useEffect(() => {
    const fetchDataAndVerification = async () => {
      if (isLoggedIn) {
        setLoading(true);
        // Fetch KYC URL
        const data = await getKycURL({});
        setKycData(data);

        // If verification ID exists, fetch verification status
        if (data?.verification_id) {
          const verificationData = await getKycVerification({
            verification_id: data.verification_id,
          });

          if (
            verificationData.status === "completed" &&
            verificationData.verified === true &&
            kyc_param === "done"
          ) {
            await checkUserLoggedIn();
            const nftData = await mintNFT();
            if (!nftData.success)
              Swal.fire({
                title: "An error occurred during the membership NFT airdrop",
                position: "bottom-end",
                html: `<p class="text-dark">
          We encountered an error during the membership NFT airdrop process. 
          We apologize for any inconvenience this may have caused.
        </p>
        <p class="text-dark">
          Please contact our support team at 
          <a 
            href="mailto:tech@rexla.com" 
            className="underline font-medium hover:text-red-400"
          >
            tech@rexla.com
          </a> with details about this issue.</p>`,
                icon: "error",
                showConfirmButton: false,
              });
            else if (nftData.success && nftData.isNew)
              Swal.fire({
                title: "Welcome to RXG!",
                html: `Your membership NFT has been successfully sent to your wallet. You now have full access to all membership benefits.`,
                icon: "success",
              });
            onNext();
          }

          if (
            verificationData.status === "completed" &&
            verificationData.verified === false
          ) {
            setVerificationStatus("failed");
          }
        }
        setLoading(false);
      }
    };

    fetchDataAndVerification();

    /* Commented socket implementation
    if (kycData && kycData.status) {
      console.log("Updated KYC data:", kycData);
      setVerificationStatus("In Progress");
    }
  
    socket.on("kycUpdate", (data) => {
      console.log(data);
      setVerificationStatus(data.verified ? "Verified" : "Failed");
    });
  
    return () => {
      socket.off("kycUpdate");
    }; */
  }, [isLoggedIn, kyc_param]);

  const reVerify = async () => {
    if (userInfo) {
      const data = await getKycURL({
        reVerify: true,
      });
      setKycData(data);
      setVerificationStatus("inProgress");
    }
  };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  /* const renderStatusMessage = () => {
    switch (verificationStatus) {
      case "Not Started":
        return "Please start the KYC process.";
      case "In Progress":
        return "Your KYC verification is in progress. Please complete the process in the form below.";
      case "Verified":
        return "Your KYC verification is complete!";
      case "Failed":
        return "KYC verification failed. Please try again or contact support.";
      default:
        return "";
    }
  }; */

  return (
    <div className="kyc w-100">
      <div className="row">
        <div className="col-2 text-start">
          <img className="section-logo" src={logo} alt="logo" />
        </div>
        <div className="col-8 section-title-block">
          {!isMobile && (
            <>
              <h2 className="section-title">Prove you are you</h2>
              <p>Complete the KYC process for verification</p>
            </>
          )}
        </div>
        <div className="col-2 text-end">
          <InfoModal className="fa fa-info-circle" />
        </div>
      </div>
      {isMobile && (
        <div className="section-title-block">
          <h2 className="section-title">Prove you are you</h2>
          <p>Complete the KYC process for verification</p>
        </div>
      )}

      {/* <div className="kyc-status-message">
        <p>{renderStatusMessage()}</p>
      </div> */}

      {isLoading || loading ? (
        <div className="d-flex align-items-center justify-content-center flex-column flex-fill">
          <div
            className="spinner-border text-primary"
            style={{ width: "4rem", height: "4rem" }}
            role="status"
          >
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        kycData &&
        kycData.form_url && (
          <iframe
            src={kycData.form_url}
            className="kyc-iframe"
            id="kyc_iframe"
            title="AML Bot KYC"
            width="100%"
            height={
              containerHeight > 350
                ? containerHeight -
                  (verificationStatus === "failed" || userInfo?.is_verified
                    ? 320
                    : 250)
                : 200
            }
            style={{ border: "none" }}
            allow="camera; microphone;"
            /* sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-downloads allow-modals" */
          />
        )
      )}

      {verificationStatus === "failed" && (
        <div>
          <button
            className="btn btn-outline-primary mt-2"
            onClick={() => reVerify()}
          >
            Verify Again
          </button>
        </div>
      )}

      {!loading && userInfo?.is_verified && (
        <div>
          <button
            className="btn btn-outline-primary mt-2"
            onClick={() => onNext()}
          >
            Next Step
          </button>
        </div>
      )}

      {isMobile ? (
        <a className="skip-link" onClick={openModal}>
          Why KYC is important?
        </a>
      ) : (
        <button
          className="btn btn-primary w-100 importnat-btn"
          onClick={openModal}
        >
          Why KYC is important?
          {/* {loading ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            "Skip (for Ambassador test)"
          )} */}
        </button>
      )}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Why KYC is Important"
        className="react-modal kyc-modal"
        overlayClassName="react-modal-overlay"
      >
        <button
          type="button"
          className="btn-close btn-close-white"
          aria-label="Close"
          onClick={closeModal}
        />
        <h2>Why KYC is Important at Rexla Global</h2>
        <div>
          <p>
            At Rexla Global, we are committed to ensuring the safety, security,
            and trust of our users. As part of this commitment, we use KYC (Know
            Your Customer) procedures to verify the identity of our users. KYC
            is a standard practice in the financial services and cryptocurrency
            sectors, where we ask for specific personal details and
            documentation to confirm your identity. This process helps us
            protect you, other users, and our platform.
          </p>
          <h3>Protecting Our Users</h3>
          <h4>1. User Safety and Security:</h4>
          <p>
            The primary reason for implementing KYC is to keep our platform
            safe. By verifying your identity, we can ensure that only legitimate
            and trusted individuals are using our services. This helps protect
            against fraud, identity theft, and other illegal activities that
            could affect you and other users.
          </p>
          <h4>2. Regulatory Compliance:</h4>
          <p>
            As a responsible company, Rexla Global must comply with legal
            requirements in various countries where we operate. KYC helps us
            meet these requirements and ensures that our platform is in line
            with local and international financial regulations. This means we
            are not only keeping your information safe but also following the
            law.
          </p>
          <h4>3. Access to Services:</h4>
          <p>
            KYC verification is a requirement to access certain features on our
            platform, including transactions, purchases, and wallet features
            where you may hold assets, tokens, or even money. Without completing
            the KYC process, you may not be able to use these services.
          </p>
          <h4>4. Account Recovery:</h4>
          <p>
            In the rare event that you lose your account details or experience
            an account compromise, KYC helps us assist you in recovering your
            account. If your account is ever stolen or hacked, having your
            identity verified makes it much easier for us to help you regain
            access and protect your assets.
          </p>

          <h4>5. Secure Documentation::</h4>
          <p>
            We take your privacy and security seriously. All documents and
            personal information provided during the KYC process are kept secure
            and confidential. We use advanced security measures to protect your
            data, and it will only be used for the purposes of verifying your
            identity and ensuring a safe experience on our platform.
          </p>

          <h4>6. Ongoing Support with Rexla Guide:</h4>
          <p>
            If you have any questions or need assistance during the KYC process,
            we encourage you to use Rexla Guide. Rexla Guide is our AI-powered
            assistant that can provide step-by-step support and answer any
            additional questions you may have. Feel free to reach out to Rexla
            Guide for help anytime during your account setup or verification
            process.
          </p>

          <h3>Your Information is Safe with Us</h3>
          <p>
            At Rexla Global, we understand the importance of protecting your
            personal information. All data submitted through the KYC process is
            stored securely and used exclusively for the purpose of identity
            verification. We follow strict data protection protocols to ensure
            your privacy and safety.
          </p>
          <p>
            In addition to keeping your information safe, KYC helps us build a
            trustworthy platform that you can rely on for secure transactions
            and asset management. We are committed to providing a safe
            environment where you can confidently use our services.
          </p>
          <p>
            KYC verification is essential for maintaining the security and
            integrity of Rexla Global. It not only helps us comply with legal
            regulations but also ensures that you, as our user, have a safe and
            seamless experience on our platform. By verifying your identity, we
            protect both your assets and your account, helping to prevent
            fraudulent activities and enabling quicker account recovery if
            needed.
          </p>
          <p>
            If you have any further questions about the KYC process or need
            assistance, don’t hesitate to reach out to Rex Guide, our virtual
            assistant, check the FAQs, or if it’s anything else contact customer
            services, who can assist you with any additional information you
            might need.
          </p>
          <p>Thank you for being a part of the Rexla Global community!</p>
        </div>
      </Modal>
    </div>
  );
};

export default KYC;
